import './history.css'
import { loadWorldContent } from '../content/loadWorldContent'
import type { HistoryEvent, Quest } from '../types'

interface QuestLink {
  quest: Quest
  /** Steps that discover this history event directly. */
  directSteps: string[]
  /** True when the quest only touches related attractions, not the event id. */
  soft: boolean
}

interface EventBundle {
  event: HistoryEvent
  direct: QuestLink[]
  soft: QuestLink[]
}

const content = loadWorldContent()
const historyIds = new Set(content.history.map((event) => event.id))

const nameById = new Map<string, string>()
for (const attraction of content.attractions) nameById.set(attraction.id, attraction.name)
for (const settlement of content.settlements) nameById.set(settlement.id, settlement.name)
for (const event of content.history) nameById.set(event.id, event.title)

function attractionIdsInQuest(quest: Quest): Set<string> {
  const ids = new Set<string>()
  for (const step of quest.steps) {
    for (const id of step.goal.ids ?? []) {
      if (!historyIds.has(id)) ids.add(id)
    }
  }
  return ids
}

function directLinksFor(eventId: string): QuestLink[] {
  const links: QuestLink[] = []
  for (const quest of content.quests) {
    const directSteps: string[] = []
    for (const step of quest.steps) {
      if (step.goal.ids?.includes(eventId)) directSteps.push(step.text)
    }
    if (directSteps.length) {
      links.push({ quest, directSteps, soft: false })
    }
  }
  return links
}

function softLinksFor(event: HistoryEvent, alreadyDirect: Set<string>): QuestLink[] {
  const related = new Set(event.relatedAttractionIds ?? [])
  if (!related.size) return []
  const links: QuestLink[] = []
  for (const quest of content.quests) {
    if (alreadyDirect.has(quest.id)) continue
    const questAttractions = attractionIdsInQuest(quest)
    const overlap = [...related].filter((id) => questAttractions.has(id))
    if (!overlap.length) continue
    links.push({
      quest,
      directSteps: overlap.map((id) => nameById.get(id) ?? id),
      soft: true,
    })
  }
  return links
}

function buildBundles(): EventBundle[] {
  return content.history.map((event) => {
    const direct = directLinksFor(event.id)
    const soft = softLinksFor(event, new Set(direct.map((link) => link.quest.id)))
    return { event, direct, soft }
  })
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (className) node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function renderQuestLinks(links: QuestLink[], soft: boolean): HTMLElement {
  const wrap = el('div')
  wrap.append(el('p', 'detail-label', soft ? 'Related via attractions' : 'Quests'))
  const ul = el('ul', 'quest-links')
  for (const link of links) {
    const li = el('li')
    const a = el('a', undefined, link.quest.title)
    a.href = `#quest-${link.quest.id}`
    li.append(a)
    const detail = link.directSteps.join(' · ')
    if (detail) {
      li.append(document.createTextNode(' — '))
      li.append(el('span', 'step-ref', detail))
    }
    ul.append(li)
  }
  wrap.append(ul)
  return wrap
}

function renderEvent(bundle: EventBundle): HTMLLIElement {
  const { event, direct, soft } = bundle
  const li = el('li', `event${direct.length ? ' has-quest' : ' gap'}`)
  li.id = `event-${event.id}`

  const card = el('article', 'event-card')
  const head = el('button', 'event-head')
  head.type = 'button'
  head.setAttribute('aria-expanded', 'false')

  head.append(el('span', 'event-when', event.when))
  head.append(el('h3', 'event-title', event.title))
  head.append(el('p', 'event-summary', event.summary))

  const flags = el('div', 'event-flags')
  if (direct.length) {
    flags.append(
      el(
        'span',
        'chip chip-quest',
        direct.length === 1 ? '1 quest' : `${direct.length} quests`,
      ),
    )
  } else {
    flags.append(el('span', 'chip chip-gap', 'No quest yet'))
  }
  if (soft.length && !direct.length) {
    flags.append(el('span', 'chip chip-soft', 'Attraction overlap'))
  }
  if (event.needsVerification) {
    flags.append(el('span', 'chip chip-warn', 'Needs verification'))
  }
  head.append(flags)

  const detail = el('div', 'event-detail')
  detail.hidden = true
  if (event.body) detail.append(el('p', 'event-body', event.body))
  if (event.note) detail.append(el('p', 'event-note', event.note))

  if (event.relatedAttractionIds?.length) {
    detail.append(el('p', 'detail-label', 'Related places'))
    const ul = el('ul', 'related')
    for (const id of event.relatedAttractionIds) {
      ul.append(el('li', undefined, nameById.get(id) ?? id))
    }
    detail.append(ul)
  }

  if (direct.length) detail.append(renderQuestLinks(direct, false))
  if (soft.length) detail.append(renderQuestLinks(soft, true))

  if (event.sources.length) {
    detail.append(el('p', 'detail-label', 'Sources'))
    const ul = el('ul', 'sources')
    for (const source of event.sources) {
      const item = el('li')
      const a = el('a', undefined, hostname(source))
      a.href = source
      a.target = '_blank'
      a.rel = 'noopener'
      item.append(a)
      ul.append(item)
    }
    detail.append(ul)
  }

  head.addEventListener('click', () => {
    const opening = detail.hidden
    detail.hidden = !opening
    card.classList.toggle('open', opening)
    head.setAttribute('aria-expanded', String(opening))
  })

  card.append(head, detail)
  li.append(card)
  return li
}

function historyHitsForQuest(quest: Quest): HistoryEvent[] {
  const hits: HistoryEvent[] = []
  const seen = new Set<string>()
  for (const step of quest.steps) {
    for (const id of step.goal.ids ?? []) {
      if (!historyIds.has(id) || seen.has(id)) continue
      seen.add(id)
      const event = content.history.find((entry) => entry.id === id)
      if (event) hits.push(event)
    }
  }
  return hits
}

function renderQuest(quest: Quest): HTMLLIElement {
  const li = el('li', 'quest-card')
  li.id = `quest-${quest.id}`
  li.append(el('p', 'quest-order', `Quest ${quest.order}`))
  li.append(el('h3', undefined, quest.title))
  li.append(el('p', 'quest-premise', quest.premise))

  const steps = el('ol', 'quest-steps')
  for (const step of quest.steps) {
    const item = el('li', undefined, step.text)
    for (const id of step.goal.ids ?? []) {
      if (!historyIds.has(id)) continue
      const hit = el('a', 'history-hit', nameById.get(id) ?? id)
      hit.href = `#event-${id}`
      item.append(hit)
    }
    steps.append(item)
  }
  li.append(steps)

  li.append(el('p', 'quest-reward', `Reward: ${quest.reward.text}`))

  const hits = historyHitsForQuest(quest)
  if (hits.length) {
    const touch = el('p', 'history-touch')
    touch.append(document.createTextNode('History: '))
    hits.forEach((event, index) => {
      if (index) touch.append(document.createTextNode(', '))
      const a = el('a', undefined, event.title)
      a.href = `#event-${event.id}`
      touch.append(a)
    })
    li.append(touch)
  }

  return li
}

function renderGap(bundle: EventBundle): HTMLLIElement {
  const li = el('li', 'gap-item')
  li.append(el('p', 'when', bundle.event.when))
  const title = el('h3')
  const a = el('a', undefined, bundle.event.title)
  a.href = `#event-${bundle.event.id}`
  title.append(a)
  li.append(title)
  li.append(el('p', undefined, bundle.event.summary))
  if (bundle.soft.length) {
    const soft = el('p', 'soft-links')
    soft.append(
      document.createTextNode(
        'Possible hooks via attractions already in: ',
      ),
    )
    bundle.soft.forEach((link, index) => {
      if (index) soft.append(document.createTextNode(', '))
      const a = el('a', undefined, link.quest.title)
      a.href = `#quest-${link.quest.id}`
      soft.append(a)
    })
    li.append(soft)
  } else {
    li.append(
      el(
        'p',
        'soft-links',
        'No overlapping quest attractions yet — a clean opening for a new quest.',
      ),
    )
  }
  return li
}

function render(): void {
  const bundles = buildBundles()
  const withQuest = bundles.filter((bundle) => bundle.direct.length > 0)
  const gaps = bundles.filter((bundle) => bundle.direct.length === 0)

  const stats = document.querySelector('#stats')
  if (stats) {
    stats.textContent = ''
    const parts = [
      ['Events', String(bundles.length)],
      ['Quests', String(content.quests.length)],
      ['Linked', String(withQuest.length)],
      ['Open', String(gaps.length)],
    ] as const
    for (const [label, value] of parts) {
      const span = el('span')
      span.append(el('strong', undefined, value))
      span.append(document.createTextNode(` ${label}`))
      stats.append(span)
    }
  }

  const timeline = document.querySelector('#timeline-list')
  if (timeline) {
    timeline.replaceChildren(...bundles.map(renderEvent))
  }

  const questList = document.querySelector('#quest-list')
  if (questList) {
    questList.replaceChildren(...content.quests.map(renderQuest))
  }

  const gapList = document.querySelector('#gap-list')
  if (gapList) {
    if (!gaps.length) {
      gapList.replaceChildren(el('li', 'gap-item', 'Every timeline event is already on a quest.'))
    } else {
      gapList.replaceChildren(...gaps.map(renderGap))
    }
  }
}

render()
